import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './store/AuthProvider';
import { useAuthStore } from './store/authStore';
import { logger } from './utils/logger';

// Страницы
import LoginModal from './pages/LoginPage';
import HomePage from './pages/HomePage';
import EventsPage from './pages/EventsPage';
import CreateEventPage from './pages/CreateEventPage';
import EventDetailPage from './pages/EventDetailPage';
import ManageEventPage from './pages/ManageEventPage';
import EditEventPage from './pages/EditEventPage';
import ProfilePage from './pages/ProfilePage';
import CallbackPage from './pages/CallbackPage';
import NotFoundPage from './pages/NotFoundPage';
import NotificationsPage from './pages/NotificationsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CommunityRulesPage from './pages/CommunityRulesPage';
import ChatsPage from './pages/ChatsPage';
import MyDataPage from './pages/MyDataPage';
import ConsentPage from './pages/ConsentPage';
import OrganizerPage from './pages/OrganizerPage';
import AdminPage from './pages/AdminPage';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import BlogCreatePage from './pages/BlogCreatePage';
import NewsPage from './pages/NewsPage';
import EventTypePage from './pages/EventTypePage';

// Компоненты
import Layout from './components/Layout';

logger.info('[App] Module loaded');

// Тема Material UI — blue/green/orange palette
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1565c0',
      dark: '#0d47a1',
      light: '#42a5f5',
    },
    secondary: {
      main: '#ef6c00',
      dark: '#e65100',
      light: '#ffa726',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: {
      main: '#2e7d32',
      light: '#66bb6a',
    },
    warning: {
      main: '#ef6c00',
    },
    error: {
      main: '#d32f2f',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
    button: { fontWeight: 600, letterSpacing: '0.02em' },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.5 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 20px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          '&:hover': { backgroundColor: '#0d47a1' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': {
            borderColor: '#cbd5e1',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  },
});

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const setLoginModalOpen = useAuthStore((s) => s.setLoginModalOpen);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
    }
  }, [isAuthenticated, setLoginModalOpen]);

  if (!isAuthenticated) {
    return <Navigate to="/events" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  
  logger.info('[AppRoutes] Rendering', { isAuthenticated, isLoading });

  return (
    <>
      <LoginModal />
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}>
          <div className="spinner" style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #1565c0',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
          }} />
          <div style={{ color: '#666' }}>Загрузка приложения...</div>
          <div style={{ color: '#999', fontSize: '12px' }}>Если загрузка длится долго, проверьте консоль</div>
        </div>
      ) : (
        <Routes>
        {/* /login открывает модальное окно и редиректит на /events */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/events" /> : <LoginRedirect />
          } 
        />
        <Route path="/auth/:provider/callback" element={<CallbackPage />} />
        
        {/* Основной layout */}
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          
          {/* Events page - доступна всем */}
          <Route path="events" element={<EventsPage />} />
          
          {/* Детали мероприятия - доступны всем */}
          <Route path="events/:id" element={<EventDetailPage />} />
          
          {/* Создание и управление требуют авторизации */}
          <Route path="events/create" element={
            <RequireAuth><CreateEventPage /></RequireAuth>
          } />
          
          <Route path="events/:id/manage" element={
            <RequireAuth><ManageEventPage /></RequireAuth>
          } />
          <Route path="events/:id/edit" element={
            <RequireAuth><EditEventPage /></RequireAuth>
          } />
          
          <Route path="profile" element={
            <RequireAuth><ProfilePage /></RequireAuth>
          } />

          <Route path="notifications" element={
            <RequireAuth><NotificationsPage /></RequireAuth>
          } />

          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />

          <Route path="terms-of-service" element={<TermsOfServicePage />} />

          <Route path="community-rules" element={<CommunityRulesPage />} />

          <Route path="chats" element={<RequireAuth><ChatsPage /></RequireAuth>} />
          <Route path="chats/:eventId" element={<RequireAuth><ChatsPage /></RequireAuth>} />

          <Route path="my-data" element={
            <RequireAuth><MyDataPage /></RequireAuth>
          } />

          <Route path="consent" element={<ConsentPage />} />

          <Route path="organizer/:id" element={<OrganizerPage />} />

          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/create" element={<RequireAuth><BlogCreatePage /></RequireAuth>} />
          <Route path="blog/:id" element={<BlogArticlePage />} />
          <Route path="blog/:id/edit" element={<RequireAuth><BlogCreatePage /></RequireAuth>} />
          <Route path="news" element={<NewsPage />} />
          <Route path="news/:id" element={<NewsPage />} />
          <Route path="event-type/:id" element={<EventTypePage />} />

          <Route
            path="admin"
            element={
              isAuthenticated && (user?.role === 'site_admin' || user?.role === 'editor') ? <AdminPage /> : <RequireAuth><AdminPage /></RequireAuth>
            }
          />
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      )}
    </>
  );
};

const LoginRedirect: React.FC = () => {
  const setLoginModalOpen = useAuthStore((s) => s.setLoginModalOpen);
  React.useEffect(() => { setLoginModalOpen(true); }, [setLoginModalOpen]);
  return <Navigate to="/events" replace />;
};

const App: React.FC = () => {
  logger.info('[App] Component mounting');
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

logger.info('[App] Module exported');

export default App;
